import { useEffect, useState } from "react";
import { ArrowUpRight, Facebook, Instagram } from "lucide-react";
import api from "@/lib/api";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { IMG } from "@/data/images";
import { useSEO } from "@/components/Seo";
import { btnGold, eyebrow } from "@/lib/styles";

export default function Join() {
  useSEO(
    "Join the Unbound | Threads Unbound™",
    "Join the Threads Unbound™ community: studio letters, first word on every drop, and a seat inside the weave."
  );
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const substack = settings?.substack_url || "";

  return (
    <main className="relative min-h-[100svh] overflow-hidden" data-testid="join-page">
      <img src={IMG.cosmic} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-[#0A0A0B]/70" />
      <div className="relative flex min-h-[100svh] items-center px-6 py-36 md:px-12 lg:px-20">
        <div className="max-w-3xl">
          <MaskedLine>
            <span className={eyebrow}>The community</span>
          </MaskedLine>
          <h1 className="mt-6 font-serif leading-[0.95] tracking-tighter">
            <MaskedLine delay={0.15}>
              <span className="block text-5xl font-light sm:text-6xl lg:text-7xl">
                Join the <span className="italic text-[#D4AF37]">Unbound</span>
              </span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
              One letter, when it matters. Studio notes, new artwork, and first access to every drop. Nothing else. We
              write like we make: rarely, and only when it is true.
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-12 flex flex-wrap items-center gap-6">
              {substack ? (
                <a
                  href={substack}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="join-substack-button"
                  className={btnGold}
                >
                  Subscribe on Substack <ArrowUpRight className="h-4 w-4" />
                </a>
              ) : (
                <span data-testid="join-substack-pending" className="border border-white/15 px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-white/30">
                  Substack link coming soon
                </span>
              )}
              <span className="text-xs uppercase tracking-[0.25em] text-white/30">Free. Unsubscribe anytime.</span>
            </div>
          </Reveal>
          {(settings?.facebook_url || settings?.instagram_url) && (
            <Reveal delay={0.55}>
              <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="join-socials">
                <span className="text-xs uppercase tracking-[0.25em] text-white/30">Or follow the thread</span>
                {settings?.facebook_url && (
                  <a
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="join-facebook-link"
                    className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  >
                    <Facebook className="h-3.5 w-3.5" /> Facebook
                  </a>
                )}
                {settings?.instagram_url && (
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="join-instagram-link"
                    className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  >
                    <Instagram className="h-3.5 w-3.5" /> Instagram
                  </a>
                )}
              </div>
            </Reveal>
          )}
          <Reveal delay={0.6}>
            <div className="mt-20 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
              {[
                ["01", "Studio letters", "Process, philosophy, and the stories behind each piece."],
                ["02", "First access", "Drops open to the list before they open to the world."],
                ["03", "No noise", "No spam, no selling your data. The thread stays clean."],
              ].map(([num, title, text]) => (
                <div key={num} className="bg-[#0A0A0B] p-6" data-testid={`join-perk-${num}`}>
                  <p className="font-serif text-sm text-[#D4AF37]/80">{num}</p>
                  <p className="mt-2 font-serif text-xl font-light">{title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/50">{text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
